<template>
  <div id="main-content" class="contact-us-page">
    <Alert
      :alertType="alert.noEmailAndPhoneNumber ? 'info' : 'error'"
      v-if="error.gettingReference || error.submitting || error.gettingMemberDetails || alert.noEmailAndPhoneNumber"
    >
      <p v-if="error.submitting">Sorry, something has gone wrong and we couldn't submit the form. Please try again, or chat to one of our friendly staff.</p>
      <p v-else-if="error.gettingMemberDetails">Sorry, something went wrong and we couldn't retrieve your contact information. Please try refreshing the page.</p>
      <p v-else-if="alert.noEmailAndPhoneNumber">
        We don't have your contact details. Please update your
        <router-link :to="toMemberDetails">Member Details</router-link>
        first.
      </p>
    </Alert>

    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
      </div>
    </div>

    <template v-if="refDataLoaded">
      <section class="row">
        <div class="col-6">
          <div class="fr-view" v-html="refData.contactUsInformationText.informationText" />
          <ContactUsForm :refDataPassed="refData" :isPublicSite="false" @post-error-state="updateSubmitErrorState" />
        </div>

        <div class="col-6">
          <div class="fr-view" v-html="refData.businessHoursInformationText.informationText" />
          <!-- Hiding LiveChat component as part of DE2E-8133 -->
          <!-- <LiveChat ref="webChat" contactUsButton /> -->
        </div>
      </section>

      <section class="row ktc-section-color">
        <div class="fr-view">
          <h3 v-if="refData.section1Title.informationText">{{ refData.section1Title.informationText }}</h3>
          <hr />
          <div class="col-8" v-html="refData.section1DescriptionText.informationText" />
          <Accordion :accBtn="refData.section1AccordionHeading1.informationText">
            <div v-html="refData.section1Accordion1Text.informationText"></div>
          </Accordion>
          <Accordion :accBtn="refData.section1AccordionHeading2.informationText">
            <div v-html="refData.section1Accordion2Text.informationText"></div>
          </Accordion>
          <Accordion :accBtn="refData.section1AccordionHeading3.informationText">
            <div v-html="refData.section1Accordion3Text.informationText"></div>
          </Accordion>
          <div class="col-8"></div>
        </div>
      </section>

      <section class="row last-row">
        <div class="col-8 fr-view">
          <h3 v-if="refData.section2Title.informationText">{{ refData.section2Title.informationText }}</h3>
          <hr />
          <div class="fr-view" v-html="refData.section2LeftText.informationText" />
        </div>
      </section>
    </template>

    <ApiLoading v-else class="loading-wrappper" :apiError="error.gettingReference">
      <p>Sorry, something has gone wrong, and we can’t show you all the information. Please try refreshing the page or chat to one of our friendly staff.</p>
    </ApiLoading>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SupportServices from '@/services/Support';
import MemberDetailsServices from '@/services/MemberDetails';
import { Alert, ApiLoading } from '@/components';
// import LiveChat from '@general/js/components/LiveChat.vue';
import ContactUsForm from '@general/js/components/ContactUsForm.vue';
import { MY_DETAILS_VIEW_UPDATE_DETAILS } from '@/router/routeNames';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import Accordion from '@/components/Accordion/Accordion.vue';

export default {
  name: 'memberPortalContactUs',

  components: {
    Alert,
    ApiLoading,
    ContactUsForm,
    Accordion,
    // LiveChat,
  },

  data() {
    return {
      refData: null,
      error: {
        gettingMemberDetails: false,
        gettingReference: false,
        submitting: false,
      },
      loading: {
        gettingMemberDetails: true,
        gettingReference: true,
      },
      alert: {
        noEmailAndPhoneNumber: false,
      },
    };
  },

  computed: {
    ...mapGetters({
      currentMember: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
      thisPolicy: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
    }),

    openingHours() {
      return this.$refs?.webChat.openingTimes;
    },

    openingHoursTimeZone() {
      return this.$refs?.webChat.thisTimezone;
    },

    refDataLoaded() {
      return !this.loading.gettingReference && !this.loading.gettingMemberDetails && !this.error.gettingReference && !this.error.gettingMemberDetails;
    },

    toMemberDetails() {
      return {
        name: MY_DETAILS_VIEW_UPDATE_DETAILS,
        params: { policyNumber: this.thisPolicy, personCode: this.currentMember.personCode },
      };
    },
  },

  async beforeMount() {
    this.getReferenceData();
    this.getMemberDetailsData();
  },

  methods: {
    async getMemberDetailsData() {
      this.loading.gettingMemberDetails = true;
      this.error.gettingMemberDetails = false;

      try {
        let data = await MemberDetailsServices.getMemberDetails(this.currentMember.personCode, this.thisPolicy);

        if (this.refData !== null) {
          data = data.contactInformation;

          // if email is not available, return empty string
          this.refData.email = data?.email || '';
          // priority of phone number is mobile>home>work and if all three are not available, return empty string
          this.refData.phoneNumber = data?.mobilePhoneNumber || data?.homePhoneNumber || data?.workPhoneNumber || '';

          if (this.refData.email === '' && this.refData.phoneNumber === '') {
            this.alert.noEmailAndPhoneNumber = true;
          }
        }
      } catch (error) {
        logger.log(error);
        this.error.gettingMemberDetails = true;
      } finally {
        this.loading.gettingMemberDetails = false;
      }
    },

    async getReferenceData() {
      this.loading.gettingReference = true;
      this.error.gettingReference = false;

      try {
        const res = await SupportServices.getContactUsReferenceData();
        this.refData = { ...res, memberName: { firstName: this.currentMember.firstName, lastName: this.currentMember.lastName } };
      } catch (error) {
        logger.log(error);
        this.error.gettingReference = true;
      } finally {
        this.loading.gettingReference = false;
      }
    },

    updateSubmitErrorState(data) {
      this.error.submitting = data;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@general/styles/_contact-us.scss';

.row {
  padding-top: spacer(3) !important;
}
</style>
